"use client";
import { Track } from "@/src/lib/tracking";

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useSnapshot } from "valtio";
import { useMemoizedFn } from "ahooks";
import {
  CustomHandlerConfig,
  WebSocketNotificationContextType,
  WSMessageFilter,
  FilterContext,
  CustomMessageHandler,
  WebSocketNotificationProviderProps,
} from "./types";
import {
  getNotificationRoute,
  buildNotificationUrl,
} from "./notificationRouter";
import {
  handleDescriptionText,
  isWSUserDataPushMessage,
  type WSUserDataPushMessage,
} from "./wsUserDataPush";
import { isWsAgentReportBatchData } from "./wsAgentReportBatchGuard";
import {
  AGENT_REPORT_CHANNEL,
  WS_PUSH_ALL_CHANNEL,
} from "@features/api/generalWS/const";
import { NOTI_AGENT_REPORT_HANDLER_ID } from "@features/notification/const";
import {
  WSAgentReportMessage,
  WSAgentReportMessageData,
  WSAgentReportMessageDataContent,
} from "@features/api/generalWS/types";
import { WebSocketManager } from "@features/api/generalWS/WebSocketManager";
import { config } from "@config/config";
import { shouldShowNotification } from "@config/notificationRoutes";
import {
  useUserDataPushNotificationContext,
  type UserDataPushNotificationConfig,
} from "@components/Notification/UserDataPushNotification";
import type { NotificationConfig } from "@components/Notification/types";
import { AgentReport, AgentReportLevelEnum } from "@features/api/memberApi";
import { NO_NEEDS_ASSET_AGENT_IDS } from "@components/CoPilot/CopilotAgent/const";
import { OnboardingListItem } from "@components/Portfolio/Onboarding/types";
import { favoritesCenter } from "@store/favorite/FavoritesCenter";
import { rootStore } from "@store/rootStore";
import { useOnboardingData } from "@components/Portfolio/Onboarding/useOnboardingData";
import { useMemberUser } from "@hooks/useMemberUser";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import { I18nUtil } from "@utils/i18n";
import { usePathname } from "@/src/hooks/usePathname";
import {
  isLoginPopupRoute,
  isOnboardingRoute,
  isPricingRoute,
  isWelcomeRoute,
} from "@/src/hooks/onboarding/isOnboardingRoute";

const WebSocketNotificationContext = createContext<
  WebSocketNotificationContextType | undefined
>(undefined);
const ENABLE_WS_MESSAGE_PERF_DEBUG = process.env.NEXT_PUBLIC_ENV !== "prod";

/**
 * 默认过滤器：检查是否订阅了该 agent
 */
const defaultSubscriptionFilter: WSMessageFilter = (item, context) => {
  const { subscribedAgentListItems } = context;
  if (!subscribedAgentListItems) {
    return false;
  }
  return subscribedAgentListItems.some((agent) => {
    const agentIds: string[] = agent?.id?.split(",") ?? [];
    return agentIds.includes(item?.extParams?.agentId ?? "");
  });
};

/**
 * 默认过滤器：检查收藏资产
 */
const defaultFavoriteFilter: WSMessageFilter = (item, context) => {
  const { allFavoriteData, noNeedsAssetAgentIds = NO_NEEDS_ASSET_AGENT_IDS } =
    context;

  // 如果该 agent 不需要判断 assetIds
  if (noNeedsAssetAgentIds.includes(item?.extParams?.agentId ?? "")) {
    return true;
  }

  if (!allFavoriteData?.data) {
    return false;
  }

  // 检查 assetIds 是否与收藏资产有交集
  const assetIds = item?.extParams?.assetIds;
  if (assetIds && assetIds.length > 0) {
    const favoriteContentIds = Object.keys(allFavoriteData.data);
    return assetIds.some((assetId) => favoriteContentIds.includes(assetId));
  }

  return false;
};

const wsLevelFilter: WSMessageFilter = (item: WSAgentReportMessageData) => {
  return item?.extParams?.level === AgentReportLevelEnum.HIGH;
};

/**
 * 将 WebSocket 消息数据转换为 AgentReport 格式
 */
const transformWSMessageToAgentReport = (
  item: WSAgentReportMessageData,
  locale?: string,
): AgentReport => {
  const contentLocale =
    I18nUtil.getBackgroundLanguageKey(locale as string) || "en-US";
  let localeTitle = item?.title ?? "";
  let localeDescription = item?.body ?? "";
  let impact = "";
  try {
    const contents = JSON.parse(
      item?.extParams?.contents,
    ) as WSAgentReportMessageDataContent[];
    const localeContent = contents.find(
      (content: WSAgentReportMessageDataContent) =>
        content.language === contentLocale,
    );
    if (localeContent) {
      localeTitle = localeContent.title;
      localeDescription = localeContent.description;
      impact = localeContent.impact;
    }
  } catch (error) {
    Track.error(error, {
      tag: "WebSocketNotification",
      msg: "[ws] Error parsing contents:",
    });
  }
  return {
    id: item?.extParams?.id ?? "",
    agent_id: item?.extParams?.agentId ?? "",
    level: (item?.extParams?.level as AgentReportLevelEnum) ?? undefined,
    type: "ALERT",
    todayCount: item?.extParams?.todayCount ?? 0,
    content: {
      title: localeTitle,
      description: localeDescription,
      impact: impact,
    },
    created: Date.now(),
  } as AgentReport;
};

/**
 * 将 AgentReport 转换为 Notification 配置
 */
const transformAgentReportToNotification = (
  report: AgentReport,
  subscribedAgentListItems?: OnboardingListItem[],
): Partial<NotificationConfig> => {
  const agentName =
    subscribedAgentListItems?.find((agent) =>
      agent?.id?.split(",")?.includes(report.agent_id ?? ""),
    )?.name ?? "";

  return {
    title: report.content?.title || "新通知",
    message: report.content?.description || "",
    level: report.level,
    time: report.created,
    extParams: {
      agentId: report.agent_id,
      assets: report.assets,
      agentName: agentName,
    },
  };
};

/**
 * WebSocket 通知 Provider
 * 管理全局 WebSocket 连接和消息分发
 */
export const WebSocketNotificationProvider: React.FC<
  WebSocketNotificationProviderProps
> = ({ children, locale, enabled = true }) => {
  const pathname = usePathname(true);
  const isInterruptiveSurface =
    isOnboardingRoute(pathname) ||
    isWelcomeRoute(pathname) ||
    isPricingRoute(pathname) ||
    isLoginPopupRoute(pathname);
  const isActive = enabled && !isInterruptiveSurface;
  const router = useShellRouter();
  const { showUserDataPushNotification, closeUserDataPushNotification } =
    useUserDataPushNotificationContext();
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { enableNotification, ownerInfo } = useSnapshot(rootStore.user);
  const { allFavoriteData } = useSnapshot(favoritesCenter.state);
  const { listItems: subscribedAgentListItems } = useOnboardingData(
    false,
    true,
  );
  const { data: memberPlanInfo } = useMemberUser();
  const subscribedAgentReport = useRef<boolean>(false);
  const wsPerfWindowRef = useRef<{
    count: number;
    totalMs: number;
    maxMs: number;
    userDataCount: number;
    agentBatchCount: number;
  }>({
    count: 0,
    totalMs: 0,
    maxMs: 0,
    userDataCount: 0,
    agentBatchCount: 0,
  });
  const wsPerfFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // 存储自定义处理器
  const customHandlersRef = useRef<Map<string, CustomHandlerConfig>>(new Map());

  // 初始化 WebSocket 客户端
  const wsClient = useMemo(() => {
    return WebSocketManager.getInstance(config.api.ws.url);
  }, []);

  const topic = useMemo(() => {
    if (!ownerInfo?.id) {
      return "";
    }
    const channel = {
      channels: [
        AGENT_REPORT_CHANNEL,
        `${WS_PUSH_ALL_CHANNEL}/${ownerInfo.id}`,
      ],
    };
    return "RG#" + JSON.stringify(channel);
  }, [ownerInfo?.id]);

  /**
   * 注册自定义处理器
   */
  const registerCustomHandler = useMemoizedFn(
    (id: string, config: CustomHandlerConfig) => {
      // 如果已存在配置，合并而不是覆盖（保留已设置的 handler）
      const existingConfig = customHandlersRef.current.get(id);
      if (existingConfig) {
        customHandlersRef.current.set(id, {
          ...existingConfig,
          ...config,
          // 如果新配置没有 handler，保留旧的 handler
          handler: config.handler ?? existingConfig.handler,
        });
      } else {
        customHandlersRef.current.set(id, config);
      }
    },
  );

  /**
   * 注销自定义处理器
   */
  const unregisterCustomHandler = useMemoizedFn((id: string) => {
    customHandlersRef.current.delete(id);
  });

  /**
   * 检查是否有自定义处理器
   */
  const hasCustomHandler = useMemoizedFn((id: string) => {
    return customHandlersRef.current.has(id);
  });

  /**
   * 更新指定处理器的过滤器上下文
   */
  const updateFilterContext = useMemoizedFn(
    (id: string, filterContext: FilterContext) => {
      const handler = customHandlersRef.current.get(id);
      if (handler) {
        customHandlersRef.current.set(id, {
          ...handler,
          filterContext,
        });
      } else {
        Track.log(
          "warn",
          "WebSocketNotification",
          `[ws] Handler ${id} not found`,
        );
      }
    },
  );

  /**
   * 更新指定处理器的 handler
   */
  const updateHandler = useMemoizedFn(
    (id: string, handler?: CustomMessageHandler) => {
      const config = customHandlersRef.current.get(id);
      if (config) {
        // 使用不可变更新模式，创建新的配置对象
        const newConfig = {
          ...config,
          handler: handler,
        };
        // 原子性地更新 Map
        customHandlersRef.current.set(id, newConfig);
      } else {
        // 如果配置不存在，创建一个新的配置（这种情况发生在组件先于 Provider 的初始化注册时）
        Track.log(
          "warn",
          "WebSocketNotification",
          `[ws][updateHandler] Handler ${id} not found, creating new config`,
        );
        customHandlersRef.current.set(id, {
          handler: handler,
          filterContext: {},
        });
      }
    },
  );

  /**
   * 更新指定处理器的 filter
   */
  const updateFilter = useMemoizedFn((id: string, filter?: WSMessageFilter) => {
    const config = customHandlersRef.current.get(id);
    if (config) {
      customHandlersRef.current.set(id, {
        ...config,
        filter,
      });
    } else {
      Track.log(
        "warn",
        "WebSocketNotification",
        `[ws] Handler ${id} not found`,
      );
    }
  });

  /**
   * 手动连接 WebSocket
   */
  const connect = useMemoizedFn(() => {
    if (!wsClient.isConnected()) {
      wsClient.connect();
    } else {
    }
  });

  /**
   * 手动断开 WebSocket
   */
  const disconnect = useMemoizedFn(() => {
    if (wsClient.isConnected()) {
      wsClient.disconnect();
    }
  });

  /**
   * 检查 WebSocket 是否已连接
   */
  const isConnected = useMemoizedFn(() => {
    return wsClient.isConnected();
  });

  /**
   * 显示默认通知
   */
  const showDefaultNotification = useMemoizedFn(
    (
      agentReport: AgentReport,
      subscribedAgentListItems?: OnboardingListItem[],
    ) => {
      // 检查是否应该显示 Notification
      // const shouldShow = shouldShowNotification(pathname);
      // log.debug(
      //   `[WebSocketNotification][ws] shouldShowNotification: ${shouldShow}`,
      // );

      // if (!shouldShow) {
      //   return;
      // }

      const baseConfig = transformAgentReportToNotification(
        agentReport,
        subscribedAgentListItems,
      );

      const route = getNotificationRoute(agentReport);
      const pushConfig: UserDataPushNotificationConfig = {
        title: baseConfig.title || "新通知",
        description: (baseConfig.message || "").trim() || undefined,
        autoClose: true,
        duration: 10000,
      };

      if (route) {
        const url = buildNotificationUrl(route);
        pushConfig.onViewMoreClick = () => {
          router.push(url);
          closeUserDataPushNotification();
        };
      }

      setTimeout(() => {
        showUserDataPushNotification(pushConfig);
      }, 3000);
    },
  );

  /**
   * wspush:all / userData：与 Agent 共用 UserDataPush 浮层（仅标题 + 描述；View More 默认打开通知中心）
   */
  const showWspushUserDataNotification = useMemoizedFn(
    (push: WSUserDataPushMessage) => {
      const payload = push.data;
      const data = handleDescriptionText(payload?.description ?? "");
      if (!data) {
        return;
      }

      const userDataConfig = {
        title: data.title,
        description: data.text || undefined,
        viewMoreHref: "#" as const,
        autoClose: true,
        duration: 10000,
      };

      setTimeout(() => {
        showUserDataPushNotification(userDataConfig);
      }, 3000);
    },
  );

  const recordWsMessagePerf = useMemoizedFn(
    (
      durationMs: number,
      messageKind: "userData" | "agentBatch" | "unknown",
    ) => {
      if (!ENABLE_WS_MESSAGE_PERF_DEBUG) {
        return;
      }
      wsPerfWindowRef.current.count += 1;
      wsPerfWindowRef.current.totalMs += durationMs;
      wsPerfWindowRef.current.maxMs = Math.max(
        wsPerfWindowRef.current.maxMs,
        durationMs,
      );
      if (messageKind === "userData") {
        wsPerfWindowRef.current.userDataCount += 1;
      } else if (messageKind === "agentBatch") {
        wsPerfWindowRef.current.agentBatchCount += 1;
      }
      if (!wsPerfFlushTimerRef.current) {
        wsPerfFlushTimerRef.current = setTimeout(() => {
          const snap = wsPerfWindowRef.current;
          const avgMs = snap.count > 0 ? snap.totalMs / snap.count : 0;
          Track.log("error", "WebSocketNotification", "[ws][perf][1s]", {
            route: pathname,
            count: snap.count,
            avgMs: Number(avgMs.toFixed(2)),
            maxMs: Number(snap.maxMs.toFixed(2)),
            userDataCount: snap.userDataCount,
            agentBatchCount: snap.agentBatchCount,
          });
          wsPerfWindowRef.current = {
            count: 0,
            totalMs: 0,
            maxMs: 0,
            userDataCount: 0,
            agentBatchCount: 0,
          };
          wsPerfFlushTimerRef.current = null;
        }, 1000);
      }
    },
  );

  /**
   * 处理 WebSocket 消息
   */
  const handleWebSocketMessage = useMemoizedFn((event: unknown) => {
    if (!isActive) {
      return;
    }
    const startAt =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    let messageKind: "userData" | "agentBatch" | "unknown" = "unknown";
    try {
      Track.log("info", "WebSocketNotification", "[ws][push] raw:", {
        cause: String(
          typeof event === "object" ? JSON.stringify(event) : String(event),
        ),
      });
    } catch {
      Track.log(
        "info",
        "WebSocketNotification",
        "[ws][push] raw: <serialize error>",
      );
    }

    if (isWSUserDataPushMessage(event)) {
      messageKind = "userData";
      const shouldHandleUserData = shouldShowNotification(pathname);
      if (!shouldHandleUserData) {
        const durationMs =
          (typeof performance !== "undefined"
            ? performance.now()
            : Date.now()) - startAt;
        recordWsMessagePerf(durationMs, messageKind);
        return;
      }
      showWspushUserDataNotification(event);
      const durationMs =
        (typeof performance !== "undefined" ? performance.now() : Date.now()) -
        startAt;
      recordWsMessagePerf(durationMs, messageKind);
      return;
    }

    const agentEvent = event as WSAgentReportMessage;
    const { data } = agentEvent;
    if (!isWsAgentReportBatchData(data)) {
      const durationMs =
        (typeof performance !== "undefined" ? performance.now() : Date.now()) -
        startAt;
      recordWsMessagePerf(durationMs, messageKind);
      return;
    }
    messageKind = "agentBatch";

    //TODO: 需要修改为不展示的路由就注销对应的处理器，或者断开WebSocket连接？
    const shouldHandle = shouldShowNotification(pathname);

    if (!shouldHandle) {
      const durationMs =
        (typeof performance !== "undefined" ? performance.now() : Date.now()) -
        startAt;
      recordWsMessagePerf(durationMs, messageKind);
      return;
    }

    //TODO: 需要增加其他消息类型时，这里其实应该是Agent Report的customHandler，需要修改customHandler的filter支持数组才行。
    const defaultFilters: WSMessageFilter[] = [
      defaultSubscriptionFilter,
      defaultFavoriteFilter,
      // defaultMemberPlanInfoFilter,
    ];

    // 检查是否有注册的配置
    // const customHandlers = Array.from(customHandlersRef.current.values());

    if (customHandlersRef.current.size > 0) {
      // 有注册的配置，分别处理

      customHandlersRef.current.forEach((handlerConfig, handlerId) => {
        // 构建过滤器列表：自定义过滤器 + 默认过滤器
        let filters: WSMessageFilter[] = [];
        if (handlerId === NOTI_AGENT_REPORT_HANDLER_ID) {
          filters = [...defaultFilters];
          if (handlerConfig.filter) {
            filters.push(handlerConfig.filter);
          }
        }

        // 应用所有过滤器
        const filteredData = data.filter((item: WSAgentReportMessageData) => {
          return filters.every((f) =>
            f(item, handlerConfig.filterContext || {}),
          );
        });

        if (filteredData.length > 0) {
          const agentReports = filteredData.map(
            (item: WSAgentReportMessageData) =>
              transformWSMessageToAgentReport(item, locale),
          );

          // 检查 handler 是否存在且有效
          const currentHandler = handlerConfig.handler;

          // 如果提供了自定义 handler，则调用自定义 handler
          if (currentHandler && typeof currentHandler === "function") {
            try {
              currentHandler(agentReports);
            } catch (error) {
              Track.log(
                "error",
                "WebSocketNotification",
                `[ws] Error executing handler ${handlerId}:`,
                { cause: String(error) },
              );
            }
          } else {
            // 没有提供 handler，使用默认通知显示策略

            // 显示 Notification（取第一条消息）
            const firstMessage = agentReports[0];
            if (firstMessage) {
              //TODO: 需要增加其他消息类型时，需要修改showDefaultNotification
              showDefaultNotification(
                firstMessage,
                handlerConfig.filterContext?.subscribedAgentListItems,
              );
            }
          }
        }
      });
    } else {
      // 没有注册任何配置，使用默认过滤器和默认通知显示

      // 使用空的 filterContext，因为没有注册配置提供上下文
      const emptyContext: FilterContext = {};

      const filteredData = data.filter((item: WSAgentReportMessageData) => {
        return defaultFilters.every((f) => f(item, emptyContext));
      });

      // 显示 Notification（取第一条消息）
      if (filteredData.length > 0) {
        const firstMessage = filteredData[0];
        if (firstMessage) {
          //TODO: 需要增加其他类型的消息时，拓展
          const agentReport = transformWSMessageToAgentReport(firstMessage);
          showDefaultNotification(agentReport);
        }
      }
    }

    const endAt =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    const durationMs = endAt - startAt;
    recordWsMessagePerf(durationMs, messageKind);
  });

  /**
   * 订阅 WebSocket 消息
   * 只负责连接和订阅，不负责更新数据上下文
   */
  useEffect(() => {
    if (!isActive) {
      if (wsClient.isConnected()) {
        wsClient.disconnect();
      }
      return;
    }
    const currentIsConnected = wsClient.isConnected();

    if (!isLoggedIn && !currentIsConnected) {
      //未登录
      return;
    } else if (!isLoggedIn && currentIsConnected) {
      //退出登录
      wsClient.disconnect();
    } else if (isLoggedIn && !enableNotification) {
      //登录但通知关闭
      return;
    } else if (isLoggedIn && currentIsConnected && !enableNotification) {
      //登录连接了但通知关闭
      wsClient.disconnect();
    } else {
      connect();
    }
    // if (!currentIsConnected) {
    //   connect();
    // }

    if (!subscribedAgentReport.current) {
      subscribedAgentReport.current = true;

      // 注册时使用空上下文，后续由数据同步 Effect 更新
      registerCustomHandler(NOTI_AGENT_REPORT_HANDLER_ID, {
        filterContext: {},
        filter: wsLevelFilter,
      });

      // 订阅主题
      const unsubscribe = wsClient.subscribe(topic, handleWebSocketMessage);

      return () => {
        unsubscribe();
        subscribedAgentReport.current = false;
        // 检查是否还有其他订阅，如果没有则断开连接
        const subscriptionInfo = wsClient.getSubscriptionInfo();
        const totalCallbacks = subscriptionInfo.reduce(
          (sum, info) => sum + info.callbackCount,
          0,
        );

        if (totalCallbacks === 0) {
          wsClient.disconnect();
        } else {
        }
      };
    }
  }, [
    topic,
    wsClient,
    handleWebSocketMessage,
    isLoggedIn,
    registerCustomHandler,
    // 移除导致循环更新的数据依赖
    // allFavoriteData,
    // subscribedAgentListItems,
    // memberPlanInfo,
    disconnect,
    connect,
    // updateFilterContext,
    enableNotification,
    isActive,
  ]);

  useEffect(() => {
    return () => {
      if (wsPerfFlushTimerRef.current) {
        clearTimeout(wsPerfFlushTimerRef.current);
        wsPerfFlushTimerRef.current = null;
      }
    };
  }, []);

  /**
   * 同步数据到过滤器上下文
   * 当数据变化时更新 context，不触发重连
   */
  useEffect(() => {
    if (isLoggedIn && enableNotification) {
      updateFilterContext(NOTI_AGENT_REPORT_HANDLER_ID, {
        allFavoriteData,
        subscribedAgentListItems,
        memberPlanInfo,
      });
    }
  }, [
    isLoggedIn,
    enableNotification,
    allFavoriteData,
    subscribedAgentListItems,
    memberPlanInfo,
    updateFilterContext,
  ]);

  const value: WebSocketNotificationContextType = {
    registerCustomHandler,
    unregisterCustomHandler,
    hasCustomHandler,
    updateFilterContext,
    updateHandler,
    updateFilter,
    connect,
    disconnect,
    isConnected,
  };

  return (
    <WebSocketNotificationContext.Provider value={value}>
      {children}
    </WebSocketNotificationContext.Provider>
  );
};

export const useOptionalWebSocketNotificationContext = () =>
  useContext(WebSocketNotificationContext);
